import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import queryString from 'query-string';

import {labels, currentResource} from '../../../../util/utils';
import HrVert from '../../../Common/Hr/HrVert';
import {assertionOrder, groupRiskStatus, reconciliationsFilterIds, routes} from '../../../../util/uiconstants';

import selectSelectedReconciliation from '../../../../reducers/Reconciliations/selectors/selectSelectedReconciliation';
import selectFilteredDataList from '../../../../reducers/Reconciliations/selectors/selectFilteredDataList';
import selectUnmappedComponentAccounts from '../../../../reducers/Reconciliations/selectors/selectUnmappedComponentAccounts';
import selectComponentAccounts from '../../../../reducers/Reconciliations/selectors/selectComponentAccounts';
import selectComponentRisks from '../../../../reducers/Reconciliations/selectors/selectComponentRisks';
import selectComponentEngagement from './../../../../reducers/Reconciliations/selectors/selectComponentEngagement';
import {
	clearSelectedReconciliation,
	getReconciliationAccounts,
	getReconciliationRisks,
	updateReconciliationAccount
} from '../../../../actions/Reconciliations';
import {getEngagementMetaData} from '../../../../actions/Engagement/engagementactions';

import OutputText from '@ey/canvascoreservices/OutputText';

import TemplateFullPage from '../../../Templates/TemplateFullPage';
import Breadcrumb from '../../../CoreWrappers/Breadcrumb';
import AccountDetails from '../../../Common/AccountDetails';
import {StyledAccountDetailsGridHeader} from '../../../Common/AccountDetails/StyledAccountDetailsGrid';
import RiskDetails from '../../../Common/RiskDetails';
import ListWithScroll from '../../../Common/ListWithScroll';
import PanelSelector from '../../../Common/PanelSelector';
import ExportWithTopDetails from '../../../Common/ExportToExcel/ExportWithTopDetails';
import ReconciliationsGenerateMail from '../../../Common/ReconciliationsGenerateMail';
import HrAlt from '../../../Common/Hr/HrAlt';
import {generateExcelWithDetailsOnTop} from './utils/generateExcelWithDetailsOnTop';
import {
	getColumnsToExport as getColumnsToExportAccount,
	getDataToExport as getDataToExportAccount
} from './utils/getColumnsToExport';
import {getColumnsToExportRisk, getDataToExportRisk} from './utils/reconciliationExportRisks';

import StyledComponentView from './StyledComponentView';
import StyledSelectorPanels from './StyledSelectorPanels';
import StyledActionBar from './StyledActionBar';
import {SUCCESS_MESSAGE} from '../../../../actions/actiontypes';
import ComponentAccountsModal from './ComponentAccountsModal';
import UnmappedRisksModal from './UnmappedRisksModal';
import selectUnmappedComponentRisks from '../../../../reducers/Reconciliations/selectors/selectUnmappedComponentRisks';
import {MIDDLE_FILE_NAME} from './utils/constants';
import selectProjectAndEngIds from '../../../../reducers/Reconciliations/selectors/selectProjectAndEngIds';

const ComponentView = () => {
	const [isLoading, setIsLoading] = useState(false);
	const {openAccounts, assessedAccounts, openRisks, assessedRisks} = reconciliationsFilterIds;
	const [filterValue, setFilterValue] = useState(openAccounts);
	const project = useSelector((store) => store.project);
	const engagementMetaData = useSelector(({engagementMetaData}) => engagementMetaData);
	const userPreferences = useSelector(({userPreferences}) => userPreferences);

	const exportToExcelLabels = currentResource.exportToExcelLabels;

	const {
		componentName,
		engagementId,
		engagementDescription,
		openAccountsCount,
		assessedAccountsCount,
		openRisksCount,
		assessedRisksCount
	} = selectSelectedReconciliation();

	const componentAccounts = selectComponentAccounts();
	const componentRisks = selectComponentRisks();
	const unmappedComponentAccounts = selectUnmappedComponentAccounts();
	const hasUnmmapedComponentAccounts = unmappedComponentAccounts.length > 0;
	const hasUnmmapedComponentRisks = selectUnmappedComponentRisks().length > 0;
	const apiConfig = selectProjectAndEngIds();
	
	const accountItems = [
		{
			id: openAccounts,
			label: labels.openAccounts,
			count: openAccountsCount,
			isAccounts: true,
			riskStatusId: [groupRiskStatus.open, groupRiskStatus.systemReOpened]
		},
		{
			id: assessedAccounts,
			label: labels.assessedAccounts,
			count: assessedAccountsCount,
			isAccounts: true,
			riskStatusId: [groupRiskStatus.assessed]
		}
	];

	const riskItems = [
		{
			id: openRisks,
			label: labels.openRisks,
			count: openRisksCount,
			isAccounts: false,
			riskStatusId: [groupRiskStatus.open, groupRiskStatus.systemReOpened]
		},
		{
			id: assessedRisks,
			label: labels.assessedRisks,
			count: assessedRisksCount,
			isAccounts: false,
			riskStatusId: [groupRiskStatus.assessed]
		}
	];

	const filterItems = [...accountItems, ...riskItems];

	const {isAccounts, riskStatusId, label: headingLabel} = filterItems.find(({id}) => filterValue === id);

	const data = selectFilteredDataList(isAccounts, riskStatusId);
	
	const allGroupRiskStatuses =  Object.keys(groupRiskStatus).map(key => groupRiskStatus[key]);
	const accountsData = selectFilteredDataList(true, allGroupRiskStatuses);
	const risksData =  selectFilteredDataList(false, allGroupRiskStatuses);

	const dispatch = useDispatch();
	const location = useLocation();
	const {componentId = null, projectId = null} = queryString.parse(location.search);
	
	useEffect(() => {
		if (projectId && componentId) handleGetComponentData();
		if(engagementMetaData === null) dispatch(getEngagementMetaData(projectId, userPreferences?.userType));
		return () => {
			dispatch(clearSelectedReconciliation());
		};
	}, [projectId, componentId]);
	
	if (!componentId || !projectId) return false;
	
	const componentEngagement = selectComponentEngagement(componentId);

	const handleGetComponentData = () => {
		setIsLoading(true);
		Promise.all([
			dispatch(getReconciliationAccounts(componentId, apiConfig)),
			dispatch(getReconciliationRisks(componentId, apiConfig))
		]).finally(() => setIsLoading(false));
	};

	const handleGroupRiskStatusChange = (primaryAccountId, groupRiskStatusId) => {
		const payload = {
			groupRiskStatusId: groupRiskStatusId
		};

		setIsLoading(true);
		dispatch(updateReconciliationAccount(primaryAccountId, payload, apiConfig))
			.then(() => {
				const successMessage =
					groupRiskStatusId === groupRiskStatus.assessed
						? labels.markAssessedSuccessMessage
						: labels.reopenSuccessMessage;
				dispatch({type: SUCCESS_MESSAGE, message: successMessage});
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const renderBreadcrumb = () => {
		const groupRiskAssessmentUrl = `${routes.gra.root}?${routes.queryString.projectId}=${projectId}${location.hash}`;

		const breadcrumbs = [
			{id: '1', label: labels.groupRiskAssessment, url: groupRiskAssessmentUrl},
			{id: '2', label: labels.reconciliations},
			{id: '3', label: componentName || ''}
		];

		const props = {
			items: breadcrumbs
		};

		return <Breadcrumb {...props} />;
	};

	const renderGenerateMail = () => {
		const props = {
			engagementId
		};

		return <ReconciliationsGenerateMail {...props}></ReconciliationsGenerateMail>
	}

	const renderExportData = () => {

		const date = new Date().toISOString().split('T')[0];

		const getExportDetails = () => {
			const worksheets = [{
				worksheetName: exportToExcelLabels.reconciliationComponentExportTabTitle,
				columns: getColumnsToExportAccount(exportToExcelLabels),
				data: getDataToExportAccount(accountsData, componentAccounts),
				isAccounts: true
			},{
				worksheetName: exportToExcelLabels.reconciliationExportTabRisks,
				columns: getColumnsToExportRisk(exportToExcelLabels),
				data: getDataToExportRisk(risksData, componentRisks),
				isAccounts: false
			}];
			
			return {
				projectName: project.name,
				fileName: `${componentName}${MIDDLE_FILE_NAME}${date}`,
				worksheets: worksheets,
				primaryTeamEngagementName: engagementDescription,
				componentTeamEngagementName: componentEngagement?.description,
			};
		};

		//end of export added code
		const props = {
			getDataToExport: getExportDetails,
			buttonTitle: exportToExcelLabels.risks.exportButtonTitle,
			labels: exportToExcelLabels,
			generateTopDetails: generateExcelWithDetailsOnTop
		};

		return <ExportWithTopDetails {...props} className="export-data" />
	} 

	const renderActionBar = () => {		
		return (
			<StyledActionBar>
				{renderGenerateMail()}
				<HrVert className="separator" />
				{renderExportData()}
			</StyledActionBar>
		);
	};

	const renderSelectors = () => {
		const sumCounts = (acc, {count}) => acc + count;

		const getTotalItem = (label, count) => ({
			id: 'total',
			label,
			count,
			inactive: true
		});

		const accountProps = {
			title: labels.primaryTeamAccountStatus,
			description: labels.accountDescription,
			items: [
				getTotalItem(labels.totalAccounts, accountItems.reduce(sumCounts, 0)),
				{separator: true},
				...accountItems
			],
			value: accountItems.some((i) => i.id === filterValue) ? filterValue : null,
			onClick: (value) => setFilterValue(value),
			modalTriggerComponent: <ComponentAccountsModal hasUnmmapedComponentAccounts={hasUnmmapedComponentAccounts} />
		};

		const riskProps = {
			title: labels.primaryTeamRiskStatus,
			description: labels.riskDescription,
			items: [getTotalItem(labels.totalRisks, riskItems.reduce(sumCounts, 0)), {separator: true}, ...riskItems],
			value: riskItems.some((i) => i.id === filterValue) ? filterValue : null,
			onClick: (value) => setFilterValue(value),
			modalTriggerComponent: <UnmappedRisksModal hasUnmmapedComponentRisks={hasUnmmapedComponentRisks} />
		};

		return (
			<StyledSelectorPanels>
				<PanelSelector {...accountProps} />
				<PanelSelector {...riskProps} />
			</StyledSelectorPanels>
		);
	};

	const renderAccountHeader = () => (
		<StyledAccountDetailsGridHeader>
			<OutputText h6 ellipsis title={labels.primaryTeamAccountName}>
				{labels.primaryTeamAccountName}
			</OutputText>
			<OutputText h6 ellipsis title={labels.designation}>
				{labels.designation}
			</OutputText>
			{assertionOrder.map((item) => (
				<OutputText h6 align="center" ellipsis title={item}>
					{item}
				</OutputText>
			))}
		</StyledAccountDetailsGridHeader>
	);

	const renderAccounts = (account) => {
		const props = {
			key: account.accountId,
			primaryAccount: account,
			componentAccounts: componentAccounts,
			onGroupRiskStatusChange: handleGroupRiskStatusChange
		};

		return <AccountDetails {...props} />;
	};

	const renderRiskHeader = () => <OutputText h6>{labels.primaryTeamRiskDesignationAndName}</OutputText>;

	const renderRisks = (risk) => {
		const props = {
			key: risk.riskId,
			primaryRisk: risk
		};

		return <RiskDetails {...props} />;
	};

	const renderList = () => {
		const props = {
			list: data,
			enableScroll: false
		};

		if (isAccounts) {
			props.item = renderAccounts;
			props.header = renderAccountHeader;
		} else {
			props.item = renderRisks;
			props.header = renderRiskHeader;
		}

		return <ListWithScroll {...props} />;
	};

	return (
		<TemplateFullPage isFlex={false}>
			<StyledComponentView isLoading={isLoading}>
				{renderBreadcrumb()}
				{data !== null && (
					<>
						<OutputText h2>{componentName}</OutputText>
						<OutputText light>{labels.reconciliationDetailInstructional}</OutputText>
						{renderActionBar()}
						{renderSelectors()}
						<div className="heading">
							<OutputText h4>{headingLabel}</OutputText>
							<HrAlt $color="--neutrals-200" />
						</div>
						{renderList()}
					</>
				)}
			</StyledComponentView>
		</TemplateFullPage>
	);
};

ComponentView.propTypes = {};

export default ComponentView;
